import React, { useState, useEffect } from 'react'
import Modal from '@mui/material/Modal'
import TransparentLoader from './../../navigationComponents/loader/TransparentLoader'
import {
    isEmpty,
    formatHour,
    formatDateNoHour,
    formatAmounts,
} from '../../utilities/generalUtils'
import { SessionTieredPricingApi } from '../../apis/SessionTieredPricing'
import { Icon } from '../../components/gp'

const TIER_BAND = {
    1: { label: 'Off-Peak', color: 'var(--gp-tou-off)', soft: 'var(--gp-tou-off-soft)' },
    2: { label: 'Normal', color: 'var(--gp-tou-norm)', soft: 'var(--gp-tou-norm-soft)' },
    3: { label: 'Peak', color: 'var(--gp-tou-peak)', soft: 'var(--gp-tou-peak-soft)' },
}

// Try to read a tier number off the phase row. The backend hasn't shipped
// it on every shape we've seen, so we fall back gracefully.
const tierOf = (row) => {
    if (typeof row?.tier === 'number') return row.tier
    if (typeof row?.priceTier === 'number') return row.priceTier
    return null
}

export default function ChargingHistoryModal(props) {
    const [sessions, setSessions] = useState({})
    const [loading, setLoading] = useState(false)

    const fetchSessionTieredPricing = async () => {
        setLoading(true)
        try {
            if (!props.sessionUUID) return
            const result = await SessionTieredPricingApi.get(props.sessionUUID)
            setSessions(result)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        if (props.open) fetchSessionTieredPricing()
    }, [props.open, props.sessionUUID])

    const phases = sessions?.phasePricing || []
    const totalEnergy = phases.reduce(
        (acc, r) => acc + (Number(r.totalKwhDelivered) || 0),
        0
    )
    const totalCost = phases.reduce(
        (acc, r) =>
            acc +
            (Number(r.totalKwhDelivered) || 0) * (Number(r.kwhPrice) || 0),
        0
    )
    const avgPrice = totalEnergy > 0 ? totalCost / totalEnergy : 0
    const totalDurationMs = phases.reduce((acc, r) => {
        if (!r?.startTime || !r?.endTime) return acc
        const start = new Date(r.startTime).getTime()
        const end = new Date(r.endTime).getTime()
        return acc + Math.max(0, end - start)
    }, 0)
    const durationLabel = (() => {
        if (!totalDurationMs) return '—'
        const totalMinutes = Math.round(totalDurationMs / 60000)
        const h = Math.floor(totalMinutes / 60)
        const m = totalMinutes % 60
        return h > 0 ? `${h}h ${m}m` : `${m}m`
    })()

    const hasData = !isEmpty(sessions) && phases.length > 0

    const tiles = [
        { label: 'Duration', value: durationLabel },
        { label: 'Energy', value: `${totalEnergy.toFixed(2)} kWh` },
        { label: 'Avg price', value: formatAmounts(avgPrice) },
        { label: 'Total', value: formatAmounts(totalCost) },
    ]

    return (
        <Modal open={props.open} onClose={props.handleClose} aria-labelledby="session-detail-title">
            <div
                style={{
                    position: 'fixed',
                    inset: 0,
                    background: 'rgba(11, 41, 38, 0.45)',
                    backdropFilter: 'blur(2px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 24,
                }}
                onMouseDown={(e) => {
                    if (e.target === e.currentTarget) props.handleClose()
                }}
            >
                <div
                    style={{
                        background: 'var(--gp-card)',
                        borderRadius: 'var(--gp-r-xl)',
                        width: '100%',
                        maxWidth: 640,
                        boxShadow: '0 20px 60px rgba(11, 41, 38, 0.25)',
                        overflow: 'hidden',
                        outline: 'none',
                        maxHeight: '90vh',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    {/* Header */}
                    <header
                        style={{
                            padding: '20px 24px 16px',
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            borderBottom: '1px solid var(--gp-hairline)',
                        }}
                    >
                        <div>
                            <div
                                style={{
                                    font: 'var(--gp-micro)',
                                    color: 'var(--gp-ink-mute)',
                                    letterSpacing: 0.6,
                                    textTransform: 'uppercase',
                                }}
                            >
                                Charging session
                            </div>
                            <h2
                                id="session-detail-title"
                                style={{
                                    margin: '4px 0 0',
                                    font: '500 20px/1.2 var(--gp-font)',
                                    color: 'var(--gp-ink)',
                                }}
                            >
                                {props.sessionDate
                                    ? formatDateNoHour(props.sessionDate)
                                    : 'Session detail'}
                            </h2>
                            {props.sessionPeakWatts && (
                                <div
                                    style={{
                                        font: 'var(--gp-caption)',
                                        color: 'var(--gp-ink-mute)',
                                        marginTop: 4,
                                    }}
                                >
                                    Peak power · {props.sessionPeakWatts}
                                </div>
                            )}
                        </div>
                        <button
                            type="button"
                            onClick={props.handleClose}
                            aria-label="Close"
                            style={{
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                border: 'none',
                                background: 'var(--gp-surface)',
                                color: 'var(--gp-ink-mute)',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'background 0.15s ease, color 0.15s ease',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'var(--gp-hairline)'
                                e.currentTarget.style.color = 'var(--gp-ink)'
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'var(--gp-surface)'
                                e.currentTarget.style.color = 'var(--gp-ink-mute)'
                            }}
                        >
                            <Icon name="close" size={16} stroke={2} />
                        </button>
                    </header>

                    {loading && (
                        <div style={{ padding: 32, display: 'flex', justifyContent: 'center' }}>
                            <TransparentLoader />
                        </div>
                    )}

                    {!loading && hasData && (
                        <>
                            {/* Summary tiles */}
                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(4, 1fr)',
                                    borderBottom: '1px solid var(--gp-hairline)',
                                }}
                            >
                                {tiles.map((s, i) => (
                                    <div
                                        key={s.label}
                                        style={{
                                            padding: '18px 20px',
                                            borderRight:
                                                i < tiles.length - 1
                                                    ? '1px solid var(--gp-hairline)'
                                                    : 'none',
                                        }}
                                    >
                                        <div
                                            style={{
                                                font: 'var(--gp-micro)',
                                                color: 'var(--gp-ink-mute)',
                                                letterSpacing: 0.4,
                                                textTransform: 'uppercase',
                                            }}
                                        >
                                            {s.label}
                                        </div>
                                        <div
                                            style={{
                                                marginTop: 4,
                                                font: '500 22px/1.1 var(--gp-font)',
                                                color: 'var(--gp-ink)',
                                                letterSpacing: -0.4,
                                            }}
                                        >
                                            {s.value}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Phase table */}
                            <div style={{ padding: '8px 8px 16px', overflowY: 'auto' }}>
                                <table
                                    style={{
                                        width: '100%',
                                        borderCollapse: 'collapse',
                                        font: 'var(--gp-body)',
                                    }}
                                >
                                    <thead>
                                        <tr>
                                            {['Time', 'Energy', 'Price', 'Total cost'].map(
                                                (h, i) => (
                                                    <th
                                                        key={h}
                                                        style={{
                                                            textAlign: i === 0 ? 'left' : 'right',
                                                            padding: '12px 16px',
                                                            font: 'var(--gp-micro)',
                                                            color: 'var(--gp-ink-mute)',
                                                            letterSpacing: 0.6,
                                                            textTransform: 'uppercase',
                                                            fontWeight: 500,
                                                            borderBottom:
                                                                '1px solid var(--gp-hairline)',
                                                        }}
                                                    >
                                                        {h}
                                                    </th>
                                                )
                                            )}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {phases.map((row, i) => {
                                            const tier = tierOf(row)
                                            const band = tier ? TIER_BAND[tier] : null
                                            const energy = `${row.totalKwhDelivered} kWh`
                                            const price = `${formatAmounts(row.kwhPrice)}/kWh`
                                            const cost = formatAmounts(
                                                (row.totalKwhDelivered || 0) *
                                                    (row.kwhPrice || 0),
                                                true
                                            )
                                            return (
                                                <tr key={i}>
                                                    <td
                                                        style={{
                                                            padding: '14px 16px',
                                                            color: 'var(--gp-ink)',
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 10,
                                                                flexWrap: 'wrap',
                                                            }}
                                                        >
                                                            {band && (
                                                                <span
                                                                    style={{
                                                                        width: 8,
                                                                        height: 8,
                                                                        borderRadius: 2,
                                                                        background: band.color,
                                                                        flexShrink: 0,
                                                                    }}
                                                                />
                                                            )}
                                                            <span>
                                                                {formatHour(row.startTime)} –{' '}
                                                                {formatHour(row.endTime)}
                                                            </span>
                                                            {band && (
                                                                <span
                                                                    style={{
                                                                        display: 'inline-flex',
                                                                        alignItems: 'center',
                                                                        padding: '3px 10px',
                                                                        borderRadius: 999,
                                                                        background: band.soft,
                                                                        color: 'var(--gp-ink)',
                                                                        font:
                                                                            'var(--gp-micro)',
                                                                        letterSpacing: 0.3,
                                                                    }}
                                                                >
                                                                    {band.label}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td
                                                        style={{
                                                            padding: '14px 16px',
                                                            color: 'var(--gp-ink)',
                                                            textAlign: 'right',
                                                            font: '400 14px/1 var(--gp-mono)',
                                                        }}
                                                    >
                                                        {energy}
                                                    </td>
                                                    <td
                                                        style={{
                                                            padding: '14px 16px',
                                                            color: 'var(--gp-ink)',
                                                            textAlign: 'right',
                                                            font: '400 14px/1 var(--gp-mono)',
                                                        }}
                                                    >
                                                        {price}
                                                    </td>
                                                    <td
                                                        style={{
                                                            padding: '14px 16px',
                                                            color: 'var(--gp-ink)',
                                                            textAlign: 'right',
                                                            font: '500 14px/1 var(--gp-mono)',
                                                        }}
                                                    >
                                                        {cost}
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>

                                {props.sessionPowerOutages?.length > 0 && (
                                    <div
                                        style={{
                                            marginTop: 16,
                                            padding: '12px 14px',
                                            background: 'var(--gp-warn-soft)',
                                            border: '1px solid #F0DDC5',
                                            borderRadius: 'var(--gp-r-md)',
                                            color: 'var(--gp-warn)',
                                            font: 'var(--gp-caption)',
                                        }}
                                    >
                                        {props.sessionPowerOutages.map((outage) => (
                                            <div
                                                key={`outage-${outage.timestamp}`}
                                                style={{ marginTop: 2 }}
                                            >
                                                Power outage at{' '}
                                                <strong>
                                                    {formatHour(outage.timestamp)}
                                                </strong>{' '}
                                                on {formatDateNoHour(outage.timestamp)}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {!loading && !hasData && (
                        <div
                            style={{
                                padding: '32px 24px',
                                textAlign: 'center',
                                font: 'var(--gp-body)',
                                color: 'var(--gp-ink-mute)',
                            }}
                        >
                            No pricing data available for this session.
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    )
}
